import * as Phaser from "phaser-ce"
import { log } from "../../common/Logging"
import { ThrobberSprite } from "../generic-sprites/ThrobberSprite"
import { Scale } from "./Scale"

export class CameraZoom {

    /**
     *
     * @returns {ZoomInfo} You can undo the zoom effect with this.
     */
    static zoom = (game: Phaser.Game, target: ZoomTarget | Phaser.Sprite, zoomDurationMillis = 1000, padding = 0): ZoomInfo => {

        const zoomInfo = CameraZoom.createZoomInfo(game, target, zoomDurationMillis, padding)

        const undoZoomInfo = new ZoomInfo()
        undoZoomInfo.zoomIncrements = zoomInfo.zoomIncrements.slice().reverse()
        undoZoomInfo.game = game
        undoZoomInfo.zoomLoopDurationMillis = zoomInfo.zoomLoopDurationMillis

        // log.debug("zoom infos: " + JSON.stringify(zoomInfo.zoomIncrements))

        CameraZoom.performZoom(zoomInfo)
        return undoZoomInfo
    }

    static createZoomInfo = (game: Phaser.Game, target: ZoomTarget | Phaser.Sprite, zoomDurationMillis = 1000, padding = 0): ZoomInfo => {
        const framesPerSecond = 24
        const frameCount = zoomDurationMillis / 1000 * framesPerSecond

        const zoomLoopDurationMillis = zoomDurationMillis / frameCount

        const worldWidth = game.width
        const worldHeight = game.height
        const worldRatio = worldWidth / worldHeight
        const targetWidth = target.width + padding * 2
        const targetHeight = target.height + padding * 2
        const targetXWithPadding = target.x - padding
        const targetYWithPadding = target.y - padding
        const targetRatio = targetWidth / targetHeight
        let targetScale: number
        let leftPadding: number
        let topPadding: number
        if (worldRatio > targetRatio) {
            // center for X
            targetScale = worldHeight / targetHeight
            const bigWidth = targetHeight * worldWidth / worldHeight
            leftPadding = (bigWidth - targetWidth) * targetScale / 2
        } else {
            // center for Y
            targetScale = worldWidth / targetWidth
            const bigHeight = targetWidth * worldHeight / worldWidth
            topPadding = (bigHeight - targetHeight) * targetScale / 2
        }

        const cameraX = game.camera.x
        const cameraY = game.camera.y
        const targetX = targetXWithPadding * targetScale - (leftPadding ? leftPadding : 0)
        const targetY = targetYWithPadding * targetScale + (topPadding ? topPadding : 0)
        log.debug("camera x: " + cameraX + " camera y: " + cameraY)
        log.debug(" targetX " + targetX + " targetY " + targetY)
        log.debug(" leftPadding: " + leftPadding + " topPadding: " + topPadding)
        log.debug("targetWidth: " + targetWidth + " targetHeight: " + targetHeight)

        log.debug("world ratio: " + worldRatio)
        log.debug("target ratio: " + targetRatio)
        log.debug("target scale: " + targetScale)

        const zoomIncrements: Array<CameraZoomIncrement> = []

        const partXChange = (targetX - cameraX) / frameCount
        const partYChange = (targetY - cameraY) / frameCount
        const partScaleChange = (1 - targetScale) / frameCount
        for (let i = 0; i < frameCount; i++) {
            const increment = new CameraZoomIncrement()
            increment.x = cameraX + partXChange * i
            increment.y = cameraY + partYChange * i
            increment.scale = 1 - partScaleChange * i
            zoomIncrements[i] = increment
        }
        const lastIncrement = new CameraZoomIncrement()
        lastIncrement.x = targetX
        lastIncrement.y = targetY
        lastIncrement.scale = targetScale
        zoomIncrements[frameCount - 1] = lastIncrement

        const zoomInfo = new ZoomInfo()
        zoomInfo.zoomIncrements = zoomIncrements
        zoomInfo.game = game
        zoomInfo.zoomLoopDurationMillis = zoomLoopDurationMillis

        return zoomInfo
    }

    static performZoom = (zoomInfo: ZoomInfo) => {
        const timer = zoomInfo.game.time.create(false)
        timer.loop(zoomInfo.zoomLoopDurationMillis, () => {
            const increment = zoomInfo.zoomIncrements[0]
            // log.debug("Current increment: " + JSON.stringify(increment))
            CameraZoom.zoomToIncrement(increment, zoomInfo.game)
            zoomInfo.zoomIncrements.shift()
            if (zoomInfo.zoomIncrements.length === 0) {
                timer.destroy()
            }
        })
        timer.start()
    }

    static resetZoom = (game: Phaser.Game) => {
        const zoom = new ZoomInfo()
        zoom.game = game
        zoom.zoomIncrements = [{x: 0, y: 0, scale: 1}]
        CameraZoom.instaZoom(zoom)
    }

    static instaZoom = (zoomInfo: ZoomInfo) => {
        const lastIncrement = zoomInfo.zoomIncrements.pop()
        CameraZoom.zoomToIncrement(lastIncrement, zoomInfo.game)
    }

    private static zoomToIncrement = (increment: CameraZoomIncrement, game: Phaser.Game) => {
        game.world.scale.setTo(increment.scale)
        game.camera.x = increment.x
        game.camera.y = increment.y
    }

    static moveSprite = (move: Phaser.Sprite | Phaser.Image, moveToX?: number, moveToY?: number, moveDuration = 1000): Phaser.Tween => {
        const realMoveToX = Math.floor(moveToX ? moveToX : move.x)
        const realMoveToY = Math.floor(moveToY ? moveToY : move.y)
        log.debug("moving sprite from " + move.x + +" " + move.y + "\nto " + realMoveToX + " " + realMoveToY)
        return move.game.add.tween(move).to({
            x: realMoveToX,
            y: realMoveToY
        }, moveDuration, Phaser.Easing.Linear.None, true)
    }

    static moveThrobber = (move: ThrobberSprite, moveToX?: number, moveToY?: number, moveDuration = 1000) => {
        const realMoveToX = Math.floor(moveToX ? moveToX : move.x)
        const realMoveToY = Math.floor(moveToY ? moveToY : move.y)
        move.game.add.tween(move).to({
            defaultX: realMoveToX,
            defaultY: realMoveToY
        }, moveDuration, Phaser.Easing.Linear.None, true)
    }

    static zoomSprite = (
        sprite: Phaser.Sprite | Phaser.Image,
        targetX?: number,
        targetY?: number,
        targetScale = 1,
        duration = 1000,
        onComplete: () => void | undefined = undefined
    ) => {

        if (!targetX) {
            targetX = sprite.x
        }
        if (!targetY) {
            targetY = sprite.y
        }

        const realTargetScale = targetScale * Scale.scaleAmount()
        const movementTween = sprite.game.add.tween(sprite).to({x: Math.floor(targetX), y: Math.floor(targetY)},
            duration, Phaser.Easing.Linear.None, true)
        sprite.game.add.tween(sprite.scale).to({x: realTargetScale, y: realTargetScale},
            duration, Phaser.Easing.Linear.None, true)
        if (onComplete != null) {
            movementTween.onComplete.addOnce(onComplete)
        }
    }

    // Ideally might be better to have this provide a vertexY but no X, and provide endX + endY
    static moveSpriteAlongParabola = (sprite: Phaser.Sprite, endX: number, vertexX: number, vertexY: number, duration = 1000) => {
        log.debug(`Move sprite along parabola start: (${sprite.x}, ${sprite.y}) end: (${endX},?), vertex: (${vertexX},${vertexY})`)
        const a = (sprite.y - vertexY) / Math.pow((sprite.x - vertexX), 2)
        const b = -2 * a * vertexX
        const c = sprite.y - (a * Math.pow(sprite.x, 2)) - b * sprite.x

        const yForX = (x: number): number => {
            return a * Math.pow(x, 2) + b * x + c
        }

        const loopDelay = 20
        const xUpdatePoints = duration / loopDelay
        const distanceTraveled = endX - sprite.x
        const goingRight = endX > sprite.x
        const xTravelIncrement = distanceTraveled / xUpdatePoints * (goingRight ? -1 : 1)
        log.debug("travel increment: " + xTravelIncrement)

        let currentX = sprite.x
        const timer = sprite.game.time.create()
        timer.loop(loopDelay, () => {
            currentX += xTravelIncrement
            sprite.x = currentX
            sprite.y = yForX(currentX)
            // log.debug("Current frog parabola position x: " + currentX + " y: " + sprite.y)
            // log.debug("Frog going right: " + goingRight + " endX " + endX)

            // if ((goingRight && currentX >= endX) || (!goingRight && currentX <= endX)) {
            if (currentX <= endX) {
                log.debug("destroying because frog currentX is less than endX")
                sprite.x = endX
                sprite.y = yForX(endX)
                timer.destroy()
            }
        })
        timer.start()
    }

}

export class ZoomInfo {
    game: Phaser.Game
    zoomIncrements: Array<CameraZoomIncrement>
    zoomLoopDurationMillis: number
}

export class ZoomTarget {
    x: number
    y: number
    width: number
    height: number

    constructor(x?: number, y?: number, w?: number, h?: number) {
        this.x = x
        this.y = y
        this.width = w
        this.height = h
    }
}

class CameraZoomIncrement {
    x: number
    y: number
    scale: number
}
